import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { scheduleOnce } from '@ember/runloop';

import { PopularLazyLoadSelector } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import makeArray from 'webapp/helpers/make-array';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer from 'webapp/modifiers/visible-check-container';
import willDestroy from 'webapp/modifiers/will-destroy';
import type { Args } from 'webapp/components/boards/select/base';
import BoardsSelectBaseComponent, { FilterSortType } from 'webapp/components/boards/select/base';
import BoardsSelectGridItemComponent from 'webapp/components/boards/select/grid-item';
import BoardsSelectTableHeaderSorterComponent from 'webapp/components/boards/select/table-header-sorter';
import CommonListLazyLoadComponent from 'webapp/components/common/list/lazy-load';
import CommonListLoaderComponent from 'webapp/components/common/list/loader';
import FilterLineComponent from 'webapp/components/filter/line';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';

import type Board from 'webapp/models-custom/board';
import type BoardFromAnotherSpace from 'webapp/models-custom/board-from-another-space';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSortBoardAtSelectPopupInGrid from 'webapp/models-custom/filter-sort/board-at-select-popup-in-grid';
import FilterSortBoardFromAnotherSpaceAtSelectPopupInGrid from 'webapp/models-custom/filter-sort/board-from-another-space-at-select-popup-in-grid';
import FilterSortReportAtSelectPopupInGrid from 'webapp/models-custom/filter-sort/report-at-select-popup-in-grid';
import type Report from 'webapp/models-custom/report';

export default class BoardsSelectGridComponent extends BoardsSelectBaseComponent<Args> {
	override filterSort:
		| FilterSortBoardAtSelectPopupInGrid
		| FilterSortBoardFromAnotherSpaceAtSelectPopupInGrid
		| FilterSortReportAtSelectPopupInGrid;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSortOptions = {
			selectors: PopularLazyLoadSelector.grid,
			onOpenView: (viewId: string) => {
				this.args.onOpenView(viewId);
			},
			getAllItems: () => {
				return this.args.model;
			},
			afterFilter: () => {
				this.triggerContentChange();
			},
		};

		const filterType = this.args.filterSortType;
		if (filterType === FilterSortType.BOARD_FROM_ANOTHER_SPACE) {
			this.filterSort = new FilterSortBoardFromAnotherSpaceAtSelectPopupInGrid(
				filterSortOptions as FilterSortSettings<BoardFromAnotherSpace>,
			);
		} else if (filterType === FilterSortType.REPORT) {
			this.filterSort = new FilterSortReportAtSelectPopupInGrid(
				filterSortOptions as FilterSortSettings<Report>,
			);
		} else {
			this.filterSort = new FilterSortBoardAtSelectPopupInGrid(
				filterSortOptions as FilterSortSettings<Board>,
			);
		}
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
	}

	@action
	override triggerContentChange(): void {
		scheduleOnce('afterRender', this, this.args.onReposition);

		// for visible-check
		const node = this.visibleCheckContainerNode;
		if (node) {
			super.triggerContentChange(node);
		}
	}
	<template>
		<FilterLineComponent
			@model={{this.filterSort}}
			@showJoinedFilterSwitchers={{this.showJoinedFilterSwitchers}}
		/>

		<CommonListLazyLoadComponent
			@classNames={{joinClasses
				(array "lazy-grid" "content" (unless this.filterSort.results "content--no-results"))
			}}
			@filterSort={{this.filterSort}}
			@initialRowCount={{5}}
			@loadMoreRowCount={{5}}
			@animationProperty="id"
			@useScrollableContainer={{true}}
			@autoLoaded={{true}}
			@bottomDistance={{350}}
			as |visibleItems exporter|
		>
			{{! Outside the wrapper, which the popup hides while nothing matches. }}
			<FilterNoResultsComponent @model={{this.filterSort}} />

			<div class="content-wrapper" {{willDestroy this.teardownView}}>
				{{! template-lint-disable table-groups }}
				<table class="flexible-table bigtable">
					<BoardsSelectTableHeaderSorterComponent
						@filterSort={{this.filterSort}}
						@isSelectedAll={{this.isSelectedAll}}
						@checkIfAllItemsCanBeSelected={{@checkIfAllItemsCanBeSelected}}
						@onSelectAll={{fn this.selectAll this.filterSort.results}}
						@hideDatasourceColumn={{this.hideDatasourceColumn}}
					/>
				</table>

				<ul
					class="flex-ct flex-ct--size-board"
					{{visibleCheckContainer}}
					{{didInsert this.setupView}}
					{{didUpdate this.triggerContentChange visibleItems}}
				>
					{{#each visibleItems as |item|}}
						<BoardsSelectGridItemComponent
							@model={{item}}
							@selected={{@selected}}
							@checkIfItemCanBeSelected={{@checkIfItemCanBeSelected}}
							@onSelect={{@onSelect}}
						/>
					{{/each}}

					{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
					{{#each (makeArray length=exporter.itemsPerRow)}}
						<li class="flex-item flex-item--fake"></li>
					{{/each}}
				</ul>
				{{#unless exporter.areAllItemsLoaded}}
					<CommonListLoaderComponent />
				{{/unless}}
			</div>
		</CommonListLazyLoadComponent>
	</template>
}
